import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { Sparkles, Loader2, Copy, ArrowLeft, Check } from "lucide-react";

// Order matters — they show as cards. Pricing is sourced live but mirrored here for the chips.
const BRAND_KINDS = [
  { key: "bio",         title: "Educator bio",          coins: 1, target: "global educators", desc: "Polished About / Bio paragraph for your profile." },
  { key: "short",       title: "Short intro · headline", coins: 1, target: "1-line tagline",   desc: "Crisp headline or 1-line intro for cards & previews." },
  { key: "long",        title: "Long professional profile", coins: 2, target: "premium recruiters", desc: "Detailed 300-word professional profile." },
  { key: "philosophy",  title: "Teaching philosophy",    coins: 2, target: "academic interviews", desc: "Reflective philosophy statement for interviews and CVs." },
  { key: "global",      title: "Global educator summary", coins: 2, target: "international schools", desc: "Internationally-positioned summary for global roles." },
  { key: "recruiter",   title: "Recruiter-friendly intro", coins: 1, target: "school recruiters", desc: "Hooks the recruiter in 4-5 sharp lines." },
  { key: "institution", title: "Institution proposal bio", coins: 2, target: "school leadership / boards", desc: "Premium bio for institutional proposals / boards." },
  { key: "speaker",     title: "Speaker / trainer bio",   coins: 2, target: "conferences / workshops", desc: "Event-stage bio for trainer / workshop hosting." },
  { key: "linkedin",    title: "LinkedIn-style About",    coins: 2, target: "LinkedIn",        desc: "Engaging LinkedIn About section in your voice." },
  { key: "parent",      title: "Parent-friendly intro",   coins: 1, target: "parents / PTM",  desc: "Warm intro to share with parents at meetings." },
];

const TONES = [
  { v: "premium",        label: "Premium" },
  { v: "warm",           label: "Warm" },
  { v: "academic",       label: "Academic" },
  { v: "parent-friendly", label: "Parent-friendly" },
  { v: "global",         label: "Global" },
  { v: "concise",        label: "Concise" },
];

export default function GrowthBranding() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [tone, setTone] = useState("premium");
  const [target, setTarget] = useState("");
  const [extra, setExtra] = useState("");
  const [variants, setVariants] = useState(3);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const meta = useMemo(() => BRAND_KINDS.find((b) => b.key === selected), [selected]);

  const generate = async () => {
    if (!selected) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/growth/branding", {
        kind: selected,
        tone,
        target: target || meta?.target,
        extra_context: extra || undefined,
        variants: Math.max(1, Math.min(5, parseInt(variants, 10) || 3)),
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          action: { label: "Recharge", onClick: () => navigate("/wallet") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not generate. Try again.");
      }
    } finally { setBusy(false); }
  };

  return (
    <Layout title="AI Personal Branding">
      <Button variant="ghost" onClick={() => navigate("/growth-studio")} className="mb-3 -ml-2" data-testid="branding-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Growth Studio
      </Button>

      <div className="mb-6">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Sparkles className="h-3 w-3" /> AI Personal Branding
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Craft your brand. <span className="text-primary">Stand out everywhere.</span>
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          10 polished, attractive, globally suitable text generators — recruiter-ready, multilingual-ready,
          and editable before you save them to your profile.
        </p>
      </div>

      {!selected && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="branding-grid">
          {BRAND_KINDS.map((b) => (
            <button
              key={b.key}
              onClick={() => { setSelected(b.key); setTarget(b.target); }}
              className="text-left tb-card p-4 hover:border-primary/40 hover:shadow-md transition group"
              data-testid={`branding-card-${b.key}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium leading-snug">{b.title}</p>
                <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200 border border-amber-200/60 dark:border-amber-500/30">
                  {b.coins} 🪙
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{b.desc}</p>
              <span className="inline-flex items-center text-[11px] text-muted-foreground mt-2 group-hover:text-primary transition">
                <Sparkles className="h-3 w-3 mr-1" /> Tap to generate
              </span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="branding-workspace">
          {/* Form */}
          <div className="lg:col-span-5 tb-card p-5 self-start sticky lg:top-6">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Generating</p>
            <p className="mt-1 font-display text-xl tracking-tight font-medium">{meta?.title}</p>
            <p className="text-xs text-muted-foreground">Exact cost shown before deduction.</p>

            <div className="mt-5 space-y-4">
              <div>
                <p className="text-[11px] font-medium mb-1.5">Tone</p>
                <div className="flex flex-wrap gap-1.5" data-testid="branding-tones">
                  {TONES.map((t) => (
                    <button
                      key={t.v}
                      onClick={() => setTone(t.v)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${
                        tone === t.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                      }`}
                      data-testid={`branding-tone-${t.v}`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[11px] font-medium mb-1.5">Target context</p>
                <Input
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder={meta?.target || "e.g. international school"}
                  className="h-10 rounded-xl"
                  data-testid="branding-target"
                />
              </div>

              <div>
                <p className="text-[11px] font-medium mb-1.5">Extra context (optional)</p>
                <Textarea
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  placeholder="Any extra hints — e.g. emphasise STEM teaching or robotics"
                  className="rounded-xl min-h-[88px]"
                  data-testid="branding-extra"
                />
              </div>

              <div>
                <p className="text-[11px] font-medium mb-1.5">Variants ({variants})</p>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={variants}
                  onChange={(e) => setVariants(e.target.value)}
                  className="w-full accent-primary"
                  data-testid="branding-variants"
                />
              </div>

              <div className="flex items-center justify-between rounded-xl bg-muted/40 px-3 py-2 border border-border">
                <p className="text-xs">
                  Exact cost: <span className="font-medium">{meta?.coins} coins</span>
                </p>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                  {meta?.coins} 🪙
                </span>
              </div>

              <Button onClick={generate} disabled={busy} className="w-full h-11 rounded-full" data-testid="branding-generate">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                {busy ? "Generating…" : `Generate ${variants} variant${variants > 1 ? "s" : ""}`}
              </Button>
              <Button variant="ghost" onClick={() => { setSelected(null); setResult(null); }} className="w-full" data-testid="branding-change">
                Choose a different generator
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-7">
            {busy && <Skeleton className="h-64" />}
            {!busy && !result && (
              <div className="tb-card p-8 text-center">
                <Sparkles className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">Configure on the left, then generate variants here.</p>
              </div>
            )}
            {result && (
              <div className="space-y-3 tb-fade-up" data-testid="branding-results">
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
                  {result.variants.length} variant{result.variants.length > 1 ? "s" : ""} · {result.cost} coins charged
                </p>
                {result.variants.map((v, i) => (
                  <VariantCard key={i} index={i + 1} text={v} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

function VariantCard({ index, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`Variant ${index} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="tb-card p-4" data-testid={`branding-variant-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Variant {index}</p>
        <Button variant="ghost" size="sm" onClick={copy} className="h-7 px-2 text-xs">
          {copied ? <Check className="h-3 w-3 mr-1 text-emerald-500" /> : <Copy className="h-3 w-3 mr-1" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <Markdown>{text}</Markdown>
      </div>
    </div>
  );
}
